import React, { useState, useEffect, useCallback, useRef } from "react";
import { WHATSAPP_NUMBER } from "../../config/constants";
import {
  Sparkles,
  Clock,
  Users,
  CheckCircle2,
  ChefHat,
  Calendar,
  Images,
  PartyPopper,
  Briefcase,
  Wine,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { fetchRawCatalogData } from "../../services/catalogService";
import ImageLightbox from "../ui/ImageLightbox";

const defaultIncludes = [
  "Estación completa de decoración: mangas pasteleras, mangas de colores y boquillas",
  "Bizcochos artesanales frescos listos para rellenar y decorar",
  "Frosting, buttercream de autor, chispas, sprinkles y toppings variados",
  "Delantales y utensilios de trabajo para cada participante",
  "Cajas individuales premium para que cada invitado se lleve su creación a casa",
  "Guía paso a paso y animación dulce dirigida por nuestra chef pastelera",
];

const eventTypes = [
  {
    icon: PartyPopper,
    title: "Eventos 🎂",
    subtitle: "Actividades Dulces & Creativas",
    desc: "Una experiencia interactiva y memorable para tu evento. Cada invitado diseña, rellena y decora su propio pastel en una actividad llena de risas que todos recordarán.",
    badge: "Actividades para Recordar",
  },
  {
    icon: Briefcase,
    title: "Team Building & Empresas 💼",
    subtitle: "Creatividad & Conexión de Equipo",
    desc: "Una dinámica relajante y diferente para grupos corporativos. Fomenta el trabajo en equipo, la risa y el compañerismo decorando pasteles.",
    badge: "Empresas & Equipos",
  },
  {
    icon: Wine,
    title: "Postres & Friends 🥂",
    subtitle: "Una Actividad Diferente",
    desc: "Momentos especiales para compartir entre amigas con una copa o café, mientras crean y personalizan su propia mini torta de revista.",
    badge: "Adultos & Amigas",
  },
];

const Workshop: React.FC = () => {
  const [config, setConfig] = useState({
    active: false,
    title: "Talleres & Experiencias Dulces para Eventos",
    badge: "Experiencias Privadas & Corporativas",
    description:
      "Llevamos la magia de la repostería artesanal directamente a tu evento. Una actividad interactiva, deliciosa y memorable donde tus invitados aprenden, se divierten y se llevan su propia torta decorada a casa.",
    duration: "2 a 3 horas",
    spots: "A partir de 6 personas",
    level: "Para todas las edades",
    date: "A domicilio o salón",
    price: "Cotización personalizada",
    includes: defaultIncludes,
    images: [] as string[],
    whatsappMsg:
      "¡Hola Estef's Cakes! 🍰 Me gustaría cotizar un taller de repostería para mi evento privado/cumpleaños. ¿Podrían brindarme información y disponibilidad?",
  });

  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  const cardsPerView = typeof window !== "undefined" && window.innerWidth >= 768 ? 3 : 1;
  const maxCarouselIndex = Math.max(0, config.images.length - cardsPerView);

  const goToNext = useCallback(() => {
    setCarouselIndex((prev) => (prev >= maxCarouselIndex ? 0 : prev + 1));
  }, [maxCarouselIndex]);

  const goToPrev = useCallback(() => {
    setCarouselIndex((prev) => (prev <= 0 ? maxCarouselIndex : prev - 1));
  }, [maxCarouselIndex]);

  useEffect(() => {
    if (isAutoPlaying && config.images.length > cardsPerView) {
      autoPlayRef.current = setInterval(goToNext, 4000);
    }
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, goToNext, config.images.length, cardsPerView]);

  useEffect(() => {
    const loadWorkshopData = async () => {
      try {
        const raw = await fetchRawCatalogData();
        const workshopRow = raw.find((row: any) => row.ID === "config_workshop");

        if (workshopRow) {
          let extraData: any = {};
          try {
            if (workshopRow["Porciones/Unidades"]) {
              extraData = JSON.parse(workshopRow["Porciones/Unidades"]);
            }
          } catch {
            // No es JSON estructurado
          }

          const imagesList = workshopRow.Imagen
            ? workshopRow.Imagen.split(",").map((s: string) => s.trim()).filter(Boolean)
            : [];

          setConfig({
            active:
              workshopRow.Disponible === undefined ||
              workshopRow.Disponible === null ||
              workshopRow.Disponible.toString().toUpperCase() !== "FALSE",
            title: workshopRow.Nombre || "Talleres & Experiencias Dulces para Eventos",
            badge: workshopRow.Badge || "Experiencias Privadas & Corporativas",
            description:
              workshopRow.Descripción ||
              "Llevamos la magia de la repostería artesanal directamente a tu evento. Una actividad interactiva, deliciosa y memorable donde tus invitados aprenden, se divierten y se llevan su propia torta decorada a casa.",
            duration: extraData.duration || "2 a 3 horas",
            spots: extraData.spots || "A partir de 6 personas",
            level: extraData.level || "Para todas las edades",
            date: extraData.date || "A domicilio o salón",
            price: extraData.price || "Cotización personalizada",
            includes:
              Array.isArray(extraData.includes) && extraData.includes.length > 0
                ? extraData.includes
                : defaultIncludes,
            images: imagesList,
            whatsappMsg:
              extraData.whatsappMsg ||
              "¡Hola Estef's Cakes! 🍰 Me gustaría cotizar un taller de repostería para mi evento privado/cumpleaños. ¿Podrían brindarme información y disponibilidad?",
          });
        }
      } catch (err) {
        console.error("Error al cargar datos del workshop:", err);
      }
    };

    loadWorkshopData();
  }, []);

  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    config.whatsappMsg
  )}`;

  if (!config.active) {
    return null;
  }

  return (
    <section
      id="workshop-section"
      className="py-12 md:py-20 relative bg-transparent"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6">

        {/* Hero del Workshop para Eventos */}
        <div
          className="relative rounded-3xl overflow-hidden mb-10 border border-rose-100 bg-white shadow-sm"
          style={{
            background:
              "linear-gradient(135deg, #fff1f2 0%, #fce7f3 50%, #fdf4ff 100%)",
          }}
        >
          {/* Blobs decorativos */}
          <div
            className="absolute top-0 right-0 w-80 h-80 rounded-full opacity-30 blur-3xl"
            style={{ background: "radial-gradient(circle, #fb7185, #e879f9)" }}
          />
          <div
            className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-20 blur-3xl"
            style={{ background: "radial-gradient(circle, #fda4af, #fce7f3)" }}
          />

          <div className="relative z-10 p-8 md:p-14 flex flex-col md:flex-row items-center gap-8">
            {/* Icono grande */}
            <div className="flex-shrink-0 w-24 h-24 md:w-32 md:h-32 rounded-3xl bg-white/90 backdrop-blur-md shadow-xl border border-rose-100 flex items-center justify-center">
              <ChefHat className="w-12 h-12 md:w-16 md:h-16 text-rose-600" />
            </div>

            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-rose-200 rounded-full px-4 py-1 mb-3.5 shadow-xs">
                <Sparkles className="w-3.5 h-3.5 text-rose-500" />
                <span className="font-body text-xs text-rose-800 font-semibold tracking-wide">
                  {config.badge}
                </span>
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold text-rose-950 mb-4 leading-tight">
                {config.title}
              </h2>
              <p className="font-body text-rose-900/80 text-base md:text-lg leading-relaxed max-w-xl whitespace-pre-line">
                {config.description}
              </p>
            </div>
          </div>
        </div>

        {/* Galería de fotos y videos del taller — Carrusel */}
        {config.images.length > 0 && (
          <div className="mb-10">
            <div className="text-center space-y-2 mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100/70 text-rose-700 text-[11px] font-bold">
                <Images className="w-3.5 h-3.5 text-rose-600" />
                <span>Galería de Talleres</span>
              </div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-rose-950">
                Momentos en Nuestros Talleres para Eventos
              </h3>
              <p className="font-body text-xs sm:text-sm text-rose-900/70 max-w-xl mx-auto">
                Inspírate con los momentos que hemos vivido en nuestros talleres para eventos, llenos de creatividad, risas y sabor.
              </p>
            </div>

            <div
              className="relative group"
              onMouseEnter={() => setIsAutoPlaying(false)}
              onMouseLeave={() => setIsAutoPlaying(true)}
            >
              <div className="overflow-hidden rounded-3xl">
                <div
                  className="flex transition-transform duration-500 ease-out"
                  style={{
                    transform: `translateX(-${carouselIndex * (100 / cardsPerView)}%)`,
                  }}
                >
                  {config.images.map((mediaUrl, i) => {
                    const isVid =
                      mediaUrl.includes(".mp4") ||
                      mediaUrl.includes(".webm") ||
                      mediaUrl.includes(".mov") ||
                      mediaUrl.includes("video");

                    return (
                      <div
                        key={i}
                        className="flex-shrink-0 px-2"
                        style={{ width: `${100 / cardsPerView}%` }}
                      >
                        <div
                          className="relative aspect-[4/3] sm:aspect-square rounded-3xl overflow-hidden bg-white border border-rose-100 shadow-sm hover:shadow-md hover:border-rose-300 transition-all cursor-zoom-in group/card"
                          onClick={() => {
                            setLightboxIndex(i);
                            setLightboxImage(mediaUrl);
                          }}
                        >
                          {isVid ? (
                            <div className="w-full h-full relative">
                              <video
                                src={mediaUrl}
                                className="w-full h-full object-cover"
                                muted
                                playsInline
                              />
                              <div className="absolute inset-0 bg-rose-950/30 flex items-center justify-center">
                                <span className="p-3 bg-white/90 text-rose-700 rounded-full shadow-lg group-hover/card:scale-110 transition-transform">
                                  ▶️
                                </span>
                              </div>
                            </div>
                          ) : (
                            <img
                              src={mediaUrl}
                              alt={`Foto del taller ${i + 1}`}
                              className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                          )}
                          <div className="absolute bottom-0 left-0 right-0 p-3.5 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity">
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] font-bold text-white flex items-center gap-1.5">
                                <Sparkles className="w-3 h-3" />
                                {isVid ? "Video del Taller" : "Momento del Taller"}
                              </span>
                              <span className="text-[10px] text-white font-semibold bg-white/20 px-2 py-0.5 rounded-full backdrop-blur-sm">
                                {isVid ? "Ver video" : "Ver foto"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Flechas de navegación */}
              {config.images.length > cardsPerView && (
                <>
                  <button
                    onClick={goToPrev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2.5 bg-white/90 hover:bg-white text-rose-700 rounded-full shadow-lg border border-rose-200 transition-all opacity-0 group-hover:opacity-100 hover:scale-110 active:scale-95 backdrop-blur-sm z-10"
                    aria-label="Imagen anterior"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={goToNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 bg-white/90 hover:bg-white text-rose-700 rounded-full shadow-lg border border-rose-200 transition-all opacity-0 group-hover:opacity-100 hover:scale-110 active:scale-95 backdrop-blur-sm z-10"
                    aria-label="Siguiente imagen"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* Indicadores de slides */}
            {config.images.length > cardsPerView && (
              <div className="flex justify-center gap-2 pt-4">
                {Array.from({ length: maxCarouselIndex + 1 }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setCarouselIndex(i)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      carouselIndex === i
                        ? "bg-rose-600 w-6"
                        : "bg-rose-300 hover:bg-rose-400"
                    }`}
                    aria-label={`Ir a slide ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Modalidades de Eventos */}
        <div className="mb-12">
          <div className="text-center mb-6">
            <p className="font-body text-xs text-rose-500 tracking-[0.2em] uppercase font-bold">
              Experiencias a la Medida
            </p>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-rose-950 mt-1">
              ¿Para qué tipo de evento buscas un taller?
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {eventTypes.map((evt, idx) => {
              const Icon = evt.icon;
              return (
                <div
                  key={idx}
                  className="bg-white rounded-3xl p-6 border border-rose-100 shadow-sm hover:shadow-md transition-all flex flex-col justify-between gap-4 group"
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center group-hover:scale-105 transition-transform">
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-rose-100/70 text-rose-700 px-2.5 py-1 rounded-full">
                        {evt.badge}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-display text-lg font-bold text-rose-950 min-h-[3rem] flex items-center">
                        {evt.title}
                      </h4>
                      <p className="text-xs font-semibold text-rose-600 mb-2">
                        {evt.subtitle}
                      </p>
                      <p className="text-xs text-rose-900/70 leading-relaxed font-body">
                        {evt.desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Info cards de datos clave */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { icon: Clock, label: "Duración", value: config.duration },
            { icon: Users, label: "Grupo Mínimo", value: config.spots },
            { icon: Sparkles, label: "Nivel Requerido", value: config.level },
            { icon: Calendar, label: "Locación", value: config.date },
          ].map(({ icon: Icon, label, value }) => (
            <div
              key={label}
              className="bg-white rounded-2xl p-5 border border-rose-100 flex flex-col items-center text-center gap-2 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center">
                <Icon className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <p className="font-body text-[11px] text-rose-400 font-semibold uppercase tracking-wider">
                  {label}
                </p>
                <p className="font-display text-sm font-bold text-rose-950 mt-0.5">
                  {value}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* ¿Qué incluye la experiencia? */}
        <div className="bg-white rounded-3xl border border-rose-100 p-7 md:p-10 mb-10 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="w-6 h-6 text-rose-600" />
            <h3 className="font-display text-2xl font-bold text-rose-950">
              ¿Qué incluye el taller para tu evento?
            </h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {config.includes.map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-3.5 rounded-2xl bg-rose-50/50 border border-rose-100/60"
              >
                <CheckCircle2 className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                <span className="font-body text-xs sm:text-sm text-rose-950 font-medium leading-relaxed">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Cotización para Eventos */}
        <div className="bg-gradient-to-r from-rose-900 to-rose-950 text-white rounded-3xl p-8 md:p-12 text-center shadow-xl relative overflow-hidden">
          <div className="relative z-10 max-w-xl mx-auto space-y-4">
            <p className="text-xs uppercase tracking-widest text-rose-200 font-bold">
              Cotiza tu Evento Dulce
            </p>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-white">
              ¿Quieres un taller de repostería en tu próxima celebración?
            </h3>
            <p className="text-sm text-rose-200/90 leading-relaxed">
              Adaptamos la experiencia según tu temática, número de invitados y locación.
            </p>
            <div className="pt-2">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-white hover:bg-rose-50 text-rose-900 font-bold px-8 py-3.5 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95 text-sm"
              >
                <MessageCircle className="w-4 h-4 text-emerald-600" />
                <span>Cotizar Taller por WhatsApp</span>
              </a>
            </div>
          </div>
        </div>

      </div>

      {/* Lightbox para fotos y videos */}
      {lightboxImage && (
        <ImageLightbox
          image={lightboxImage}
          onClose={() => setLightboxImage(null)}
          allImages={config.images}
          currentImageIndex={lightboxIndex}
        />
      )}
    </section>
  );
};

export default Workshop;
